import Curve from "@/components/site/Curve";
import { BgVideo } from "@/components/site/Video";
import defaultHero from "@/assets/footage/clip-1.mp4.asset.json";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { SITE_URL } from "@/lib/site";
import { ORG_SCHEMA } from "@/lib/seo-pages";
import MobileCallBar from "@/components/site/MobileCallBar";
import ContactDialog from "@/components/site/ContactDialog";

export const LOCAL_BUSINESS = ORG_SCHEMA;

type Props = { title: string; description: string; path: string; schema?: object[]; children: React.ReactNode };

const Layout = ({ title, description, path, schema = [], children }: Props) => (
  <>
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`${SITE_URL}${path}`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`${SITE_URL}${path}`} />
      {[LOCAL_BUSINESS, ...schema].map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
    <Navigation />
    <main>{children}</main>
    <Footer />
    <MobileCallBar />
    <ContactDialog />
  </>
);

export const faqSchema = (items: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const serviceSchema = (name: string, description: string, area: string | string[] = ["Wrexham", "North Wales", "Shrewsbury", "Shropshire"]) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  provider: { "@type": "LocalBusiness", name: "Stag Marketing Group" },
  areaServed: area,
});

export const videoSchema = (name: string, youtubeId: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  thumbnailUrl: `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
  embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
  uploadDate: "2025-01-01",
});

export const PageHero = ({ eyebrow, title, text, children, video = defaultHero.url }: { eyebrow: string; title: string; text: string; children?: React.ReactNode; video?: string }) => {
  return (
  <section className="brand-hero relative overflow-hidden pb-24 pt-36 md:pb-32 md:pt-44">
    <BgVideo src={video} />
    <Curve position="bottom" />
    <div className="relative z-10 container mx-auto max-w-4xl px-6 text-center">
      <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
      <h1 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">{title}</h1>
      <p className="mx-auto mt-6 max-w-2xl text-lg text-background md:text-xl">{text}</p>
      {children}
    </div>
  </section>
  );
};

export default Layout;
